import { useEffect, useRef, useState } from 'react';

function normalizeText(value) {
	return String(value || '').trim().toLowerCase();
}

function includesAny(value, selectedValues) {
	if (!selectedValues || !selectedValues.length) {
		return true;
	}

	return selectedValues.includes(value);
}

function matchesSeatCount(vehicleSeats, selectedSeats) {
	if (!selectedSeats || !selectedSeats.length) {
		return true;
	}

	return selectedSeats.some((seat) => {
		if (seat === '9+') {
			return vehicleSeats >= 9;
		}

		return vehicleSeats === Number(seat);
	});
}

function matchesOptions(vehicleOptions, selectedOptions) {
	if (!selectedOptions || !selectedOptions.length) {
		return true;
	}

	if (!vehicleOptions || !vehicleOptions.length) {
		return false;
	}

	return selectedOptions.every((option) => vehicleOptions.includes(option));
}

export function filterVehicles(vehicles, filters = {}) {
	const {
		query = '',
		city = '',
		vehicleType = 'all',
		budgetMax = '',
		priceMin = '',
		priceMax = '',
		startDate = '',
		endDate = '',
		transmissions = [],
		fuels = [],
		seats = [],
		agencies = [],
		options = [],
		availableOnly = false
	} = filters;

	const normalizedQuery = normalizeText(query);
	const normalizedCity = normalizeText(city);
	const normalizedBudget = budgetMax === '' || budgetMax === null ? null : Number(budgetMax);
	const normalizedPriceMin = priceMin === '' || priceMin === null ? null : Number(priceMin);
	const normalizedPriceMax = priceMax === '' || priceMax === null ? null : Number(priceMax);
	const needsDate = Boolean(startDate || endDate);

	return vehicles.filter((vehicle) => {
		if (normalizedQuery) {
			const haystack = [
				vehicle.nom,
				vehicle.agence,
				vehicle.ville,
				vehicle.type,
				vehicle.transmission,
				vehicle.carburant,
				...(vehicle.options || [])
			]
				.join(' ')
				.toLowerCase();

			if (!haystack.includes(normalizedQuery)) {
				return false;
			}
		}

		if (normalizedCity && normalizeText(vehicle.ville) !== normalizedCity) {
			return false;
		}

		if (vehicleType !== 'all' && vehicle.type !== vehicleType) {
			return false;
		}

		if (normalizedBudget !== null && !Number.isNaN(normalizedBudget) && vehicle.prixJour > normalizedBudget) {
			return false;
		}

		if (normalizedPriceMin !== null && !Number.isNaN(normalizedPriceMin) && vehicle.prixJour < normalizedPriceMin) {
			return false;
		}

		if (normalizedPriceMax !== null && !Number.isNaN(normalizedPriceMax) && vehicle.prixJour > normalizedPriceMax) {
			return false;
		}

		if (needsDate && !vehicle.disponible) {
			return false;
		}

		if (!includesAny(vehicle.transmission, transmissions)) {
			return false;
		}

		if (!includesAny(vehicle.carburant, fuels)) {
			return false;
		}

		if (!matchesSeatCount(vehicle.places, seats)) {
			return false;
		}

		if (agencies.length && !agencies.includes(vehicle.agence)) {
			return false;
		}

		if (!matchesOptions(vehicle.options || [], options)) {
			return false;
		}

		if (availableOnly && !vehicle.disponible) {
			return false;
		}

		return true;
	});
}

export function sortVehicles(vehicles, sortBy = 'relevance') {
	const sorted = [...vehicles];

	switch (sortBy) {
		case 'priceAsc':
			sorted.sort((left, right) => left.prixJour - right.prixJour || right.note - left.note);
			break;
		case 'priceDesc':
			sorted.sort((left, right) => right.prixJour - left.prixJour || right.note - left.note);
			break;
		case 'rating':
			sorted.sort((left, right) => right.note - left.note || left.prixJour - right.prixJour);
			break;
		case 'newest':
			sorted.sort((left, right) => right.id - left.id);
			break;
		default:
			sorted.sort((left, right) => Number(right.disponible) - Number(left.disponible) || right.note - left.note || left.prixJour - right.prixJour);
			break;
	}

	return sorted;
}

export function useVoiceSearch(onResult) {
	const recognitionRef = useRef(null);
	const onResultRef = useRef(onResult);
	const [isListening, setIsListening] = useState(false);
	const [error, setError] = useState('');

	useEffect(() => {
		onResultRef.current = onResult;
	}, [onResult]);

	useEffect(() => {
		const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

		if (!SpeechRecognition) {
			setError('La recherche vocale nest pas disponible sur ce navigateur.');
			return undefined;
		}

		const recognition = new SpeechRecognition();
		recognition.lang = 'fr-FR';
		recognition.interimResults = true;
		recognition.continuous = false;

		recognition.onstart = () => {
			setError('');
			setIsListening(true);
		};

		recognition.onresult = (event) => {
			const transcript = Array.from(event.results)
				.map((result) => result[0]?.transcript || '')
				.join(' ')
				.trim();

			if (transcript && typeof onResultRef.current === 'function') {
				onResultRef.current(transcript);
			}
		};

		recognition.onerror = (event) => {
			setError(event?.error === 'not-allowed'
				? 'Microphone refuse. Autorisez l acces pour utiliser la recherche vocale.'
				: 'La recherche vocale est indisponible pour le moment.');
			setIsListening(false);
		};

		recognition.onend = () => {
			setIsListening(false);
		};

		recognitionRef.current = recognition;

		return () => {
			if (recognitionRef.current) {
				recognitionRef.current.onstart = null;
				recognitionRef.current.onresult = null;
				recognitionRef.current.onerror = null;
				recognitionRef.current.onend = null;
				recognitionRef.current.stop();
				recognitionRef.current = null;
			}
		};
	}, []);

	const start = () => {
		if (!recognitionRef.current) {
			setError('La recherche vocale nest pas disponible sur ce navigateur.');
			return;
		}

		setError('');

		try {
			recognitionRef.current.start();
		} catch (recognitionError) {
			setError('Impossible de lancer la recherche vocale. Reessayez dans quelques secondes.');
		}
	};

	const stop = () => {
		if (recognitionRef.current) {
			recognitionRef.current.stop();
		}
	};

	return {
		isListening,
		start,
		stop,
		error
	};
}

